import { supabase } from "../lib/supabaseClient";

const EVENTS_TABLE = "events";
export const EVENT_NAME_MAX_LENGTH = 249;
const DUPLICATE_EVENT_NAME_ERROR = "An event with this name already exists";
const eventsListCache = new Map();
const eventsCacheSubscribers = new Map();
const pendingEventSaveVersions = new Map();
const eventSaveChains = new Map();

const requireClient = () => {
  if (!supabase) {
    throw new Error("Supabase is not configured.");
  }

  return supabase;
};

export const normalizeEventName = (name) => String(name || "").trim();

export const validateEventName = (name) => {
  const normalizedName = normalizeEventName(name);
  if (!normalizedName) {
    return "Enter an event name to continue";
  }

  if (normalizedName.length > EVENT_NAME_MAX_LENGTH) {
    return "Event name must be less than 250 characters";
  }

  return "";
};

const toEventRow = ({ badgeSize, badgeTemplate, importedSpreadsheetData, name, userId }) => ({
  user_id: userId,
  name: normalizeEventName(name) || "Untitled event",
  badge_size: badgeSize,
  badge_template: badgeTemplate,
  field_mapping: importedSpreadsheetData?.mapping ?? {},
  spreadsheet_data: importedSpreadsheetData ?? null,
});

export const fromEventRow = (row) => ({
  id: row.id,
  name: row.name,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  userId: row.user_id,
  badgeSize: row.badge_size,
  badgeTemplate: row.badge_template,
  fieldMapping: row.field_mapping ?? {},
  spreadsheetData: row.spreadsheet_data,
});

const getCachedEventsKey = (userId) => String(userId || "");

const getEventSaveKey = (userId, eventId) =>
  `${getCachedEventsKey(userId)}:${eventId}`;

const notifyEventsCacheSubscribers = (userId) => {
  const cacheKey = getCachedEventsKey(userId);
  const cachedEvents = eventsListCache.get(cacheKey);
  const subscribers = eventsCacheSubscribers.get(cacheKey);

  if (!subscribers || cachedEvents === undefined) return;

  subscribers.forEach((subscriber) => subscriber(cachedEvents));
};

const cacheEvents = (userId, events) => {
  if (!userId) return;
  eventsListCache.set(getCachedEventsKey(userId), events);
  notifyEventsCacheSubscribers(userId);
};

export const getCachedEvents = (userId) =>
  eventsListCache.get(getCachedEventsKey(userId));

export const subscribeEventsCache = (userId, subscriber) => {
  const cacheKey = getCachedEventsKey(userId);

  if (!eventsCacheSubscribers.has(cacheKey)) {
    eventsCacheSubscribers.set(cacheKey, new Set());
  }

  const subscribers = eventsCacheSubscribers.get(cacheKey);
  subscribers.add(subscriber);

  return () => {
    subscribers.delete(subscriber);
    if (!subscribers.size) {
      eventsCacheSubscribers.delete(cacheKey);
    }
  };
};

const getCachedEvent = (userId, eventId) =>
  getCachedEvents(userId)?.find((event) => event.id === eventId);

const startPendingEventSave = (saveKey) => {
  const saveVersion = (pendingEventSaveVersions.get(saveKey) || 0) + 1;
  pendingEventSaveVersions.set(saveKey, saveVersion);
  return saveVersion;
};

const isLatestPendingEventSave = (saveKey, saveVersion) =>
  pendingEventSaveVersions.get(saveKey) === saveVersion;

const finishPendingEventSave = (saveKey, saveVersion) => {
  if (isLatestPendingEventSave(saveKey, saveVersion)) {
    pendingEventSaveVersions.delete(saveKey);
  }
};

const hasPendingEventSave = (userId, eventId) =>
  pendingEventSaveVersions.has(getEventSaveKey(userId, eventId));

const isCachedEventNewer = (cachedEvent, event) => {
  if (!cachedEvent?.updatedAt || !event?.updatedAt) return false;
  return new Date(cachedEvent.updatedAt).getTime() > new Date(event.updatedAt).getTime();
};

const stringifyForCompare = (value) => JSON.stringify(value ?? null);

const areEventListItemsEqual = (event, nextEvent) =>
  event.id === nextEvent.id &&
  event.name === nextEvent.name &&
  event.createdAt === nextEvent.createdAt &&
  event.updatedAt === nextEvent.updatedAt &&
  event.userId === nextEvent.userId &&
  stringifyForCompare(event.badgeSize) === stringifyForCompare(nextEvent.badgeSize) &&
  stringifyForCompare(event.badgeTemplate) === stringifyForCompare(nextEvent.badgeTemplate) &&
  stringifyForCompare(event.spreadsheetData) === stringifyForCompare(nextEvent.spreadsheetData);

const mergeEventsWithCache = (userId, events) => {
  const cachedEvents = getCachedEvents(userId);
  if (!cachedEvents) return events;

  const cachedEventsById = new Map(
    cachedEvents.map((event) => [event.id, event])
  );

  return events.map((event) => {
    const cachedEvent = cachedEventsById.get(event.id);
    if (!cachedEvent) return event;

    if (
      hasPendingEventSave(userId, event.id) ||
      isCachedEventNewer(cachedEvent, event) ||
      areEventListItemsEqual(cachedEvent, event)
    ) {
      return cachedEvent;
    }

    return event;
  });
};

const updateCachedEvent = (userId, event, { moveToFront = false } = {}) => {
  const cachedEvents = getCachedEvents(userId);
  if (!cachedEvents) return;

  const otherEvents = cachedEvents.filter(({ id }) => id !== event.id);
  const nextEvents = moveToFront
    ? [event, ...otherEvents]
    : cachedEvents.map((cachedEvent) =>
      cachedEvent.id === event.id ? event : cachedEvent
    );
  const hasCachedEvent = cachedEvents.some(({ id }) => id === event.id);

  cacheEvents(
    userId,
    hasCachedEvent ? nextEvents : [event, ...cachedEvents]
  );
};

const removeCachedEvent = (userId, eventId) => {
  const cachedEvents = getCachedEvents(userId);
  if (!cachedEvents) return;

  cacheEvents(
    userId,
    cachedEvents.filter((event) => event.id !== eventId)
  );
};

const buildOptimisticUpdatedEvent = (eventId, userId, event, previousEvent) => {
  if (!previousEvent) return null;

  return {
    ...previousEvent,
    id: eventId,
    name: normalizeEventName(event.name) || previousEvent.name,
    userId,
    badgeSize: event.badgeSize,
    badgeTemplate: event.badgeTemplate,
    fieldMapping: event.importedSpreadsheetData?.mapping ?? previousEvent.fieldMapping,
    spreadsheetData: event.importedSpreadsheetData ?? previousEvent.spreadsheetData,
    updatedAt: new Date().toISOString(),
  };
};

export const listEvents = async (userId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(EVENTS_TABLE)
    .select("id,name,created_at,updated_at,badge_size,badge_template,spreadsheet_data")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) throw error;
  const events = mergeEventsWithCache(userId, data.map(fromEventRow));
  cacheEvents(userId, events);
  return events;
};

export const listAllEventsForAdmin = async ({ page = 1, pageSize = 9 } = {}) => {
  const client = requireClient();
  const { data, error } = await client.rpc("list_all_events_for_admin", {
    p_limit: pageSize,
    p_offset: (page - 1) * pageSize,
  });
  if (error) throw error;
  return {
    events: (data || []).map((row) => ({
      ...fromEventRow(row),
      ownerEmail: row.owner_email,
      ownerId: row.user_id,
    })),
    totalCount: Number(data?.[0]?.total_count || 0),
  };
};

export const eventNameExists = async ({ excludeEventId, name, userId }) => {
  const normalizedName = normalizeEventName(name);
  if (!userId || !normalizedName) return false;

  const client = requireClient();
  let query = client
    .from(EVENTS_TABLE)
    .select("id")
    .eq("user_id", userId)
    .eq("name", normalizedName)
    .limit(1);

  if (excludeEventId) {
    query = query.neq("id", excludeEventId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []).length > 0;
};

export const validateNewEventName = async ({ name, userId }) => {
  const nameError = validateEventName(name);
  if (nameError) return nameError;

  const hasDuplicateName = await eventNameExists({ name, userId });
  return hasDuplicateName ? DUPLICATE_EVENT_NAME_ERROR : "";
};

export const getEvent = async (eventId, userId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(EVENTS_TABLE)
    .select("*")
    .eq("id", eventId)
    .eq("user_id", userId)
    .single();

  if (error) throw error;
  const event = fromEventRow(data);
  updateCachedEvent(userId, event);
  return event;
};

export const getEventForAdmin = async (eventId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(EVENTS_TABLE)
    .select("*")
    .eq("id", eventId)
    .single();
  if (error) throw error;
  return fromEventRow(data);
};

export const createEvent = async ({ userId, ...eventInput }) => {
  const client = requireClient();
  const eventNameError = await validateNewEventName({
    name: eventInput.name,
    userId,
  });
  if (eventNameError) {
    throw new Error(eventNameError);
  }

  const { data, error } = await client
    .from(EVENTS_TABLE)
    .insert(toEventRow({ ...eventInput, userId }))
    .select("*")
    .single();

  if (error) {
    if (error.code === "23505") {
      throw new Error(DUPLICATE_EVENT_NAME_ERROR);
    }
    throw error;
  }
  const createdEvent = fromEventRow(data);
  updateCachedEvent(userId, createdEvent, { moveToFront: true });
  return createdEvent;
};

export const updateEvent = async (eventId, userId, event) => {
  const client = requireClient();
  const saveKey = getEventSaveKey(userId, eventId);
  const saveVersion = startPendingEventSave(saveKey);
  const previousCachedEvent = getCachedEvent(userId, eventId);
  const optimisticEvent = buildOptimisticUpdatedEvent(
    eventId,
    userId,
    event,
    previousCachedEvent
  );

  if (optimisticEvent) {
    updateCachedEvent(userId, optimisticEvent);
  }

  const runUpdate = async () => {
    const { data, error } = await client
      .from(EVENTS_TABLE)
      .update(toEventRow({ ...event, userId }))
      .eq("id", eventId)
      .eq("user_id", userId)
      .select("*")
      .single();

    if (error) throw error;
    return fromEventRow(data);
  };

  const previousSave = eventSaveChains.get(saveKey) || Promise.resolve();
  const savePromise = previousSave.catch(() => undefined).then(runUpdate);
  eventSaveChains.set(saveKey, savePromise);

  savePromise.then(
    () => {
      if (eventSaveChains.get(saveKey) === savePromise) {
        eventSaveChains.delete(saveKey);
      }
    },
    () => {
      if (eventSaveChains.get(saveKey) === savePromise) {
        eventSaveChains.delete(saveKey);
      }
    }
  );

  try {
    const updatedEvent = await savePromise;
    if (isLatestPendingEventSave(saveKey, saveVersion)) {
      updateCachedEvent(userId, updatedEvent);
    }
    return updatedEvent;
  } catch (error) {
    if (isLatestPendingEventSave(saveKey, saveVersion) && previousCachedEvent) {
      updateCachedEvent(userId, previousCachedEvent);
    }
    throw error;
  } finally {
    finishPendingEventSave(saveKey, saveVersion);
  }
};

export const deleteEvent = async (eventId, userId) => {
  const client = requireClient();
  const { error } = await client
    .from(EVENTS_TABLE)
    .delete()
    .eq("id", eventId)
    .eq("user_id", userId);

  if (error) throw error;
  removeCachedEvent(userId, eventId);
};
